import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useStorageAction, type IntegritySummary, type StorageAdmin } from '@/api/storage-admin';
import { serverError } from './common';

const FIGURES: { key: keyof IntegritySummary; label: string; bad?: boolean }[] = [
  { key: 'orphaned', label: 'Orphaned files' },
  { key: 'dangling', label: 'Broken links', bad: true },
  { key: 'missing', label: 'Missing contents', bad: true },
  { key: 'strays', label: 'Stray files removed' },
  { key: 'relinked', label: 'Relinked' },
  { key: 'reattached', label: 'Re-attached' },
  { key: 'verified', label: 'Contents checked' },
];

/**
 * The nightly checks that records, the file list and the stored bytes agree: what the last run found and fixed,
 * with a button to run them now. Every finding is also in Logs as an "Integrity check".
 */
export function IntegrityCard({ admin, summary }: { admin: StorageAdmin; summary: IntegritySummary | null }) {
  const run = useStorageAction(admin, () => admin.runIntegrity!());
  const [error, setError] = useState<string>();
  const last = run.data ?? summary;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: last ? 1.5 : 0 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Integrity checks</Typography>
          <Typography variant="caption" color="text.secondary">
            {last
              ? `Last run ${new Date(last.at).toLocaleString('en-IN')} · they run every night`
              : 'Not run yet — they run every night with the usage check.'}
          </Typography>
        </Box>
        {admin.runIntegrity && (
          <Button size="small" variant="outlined" disabled={run.isPending}
                  onClick={() => { setError(undefined); run.mutateAsync(undefined).catch((e) => setError(serverError(e).message)); }}>
            {run.isPending ? 'Checking…' : 'Run checks now'}
          </Button>
        )}
      </Stack>
      {last && (
        <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {FIGURES.map(({ key, label, bad }) => {
            const value = last[key] as number;
            return (
              <Box key={key}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }} color={bad && value > 0 ? 'error.main' : undefined}>
                  {value.toLocaleString('en-IN')}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}
      {last?.warnings.map((w) => <Alert key={w} severity="warning" sx={{ mt: 1.5 }}>{w}</Alert>)}
      {error && <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(undefined)}>{error}</Alert>}
    </Paper>
  );
}
